import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ClipboardList, RefreshCw, CheckCircle, XCircle, MessageSquare,
  Loader2, Inbox, ArrowLeft, Building2, User, Mail, Globe,
  FileText, Shield, MapPin, Calendar, DollarSign, Users,
  AlertTriangle, ChevronDown, ChevronUp, ChevronRight, Clock, Eye, Download
} from 'lucide-react';
import { toast } from '../../lib/safeToast';

function getDocTypeLabel(docType) {
  if (!docType) return 'Document';
  if (docType === 'other') return 'Other document';
  if (docType.startsWith('other_')) {
    const n = parseInt(docType.replace('other_', ''), 10);
    return Number.isFinite(n) ? `Other document (${n + 1})` : docType.replace(/_/g, ' ');
  }
  if (docType.startsWith('info_request_attachment_')) {
    const n = parseInt(docType.replace('info_request_attachment_', ''), 10);
    return Number.isFinite(n) ? `Info request attachment (${n + 1})` : 'Info request attachment';
  }
  return docType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function base64ToBlobUrl(dataUrl) {
  try {
    const [header, b64] = dataUrl.split(',');
    if (!b64 || b64.length < 20) return null;
    const mime = (header.match(/data:(.*?);/) || [])[1] || 'application/octet-stream';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: mime }));
  } catch {
    return null;
  }
}

function DocPreview({ doc }) {
  const [imgError, setImgError] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState(null);
  const src = doc.data_url || doc.url;
  const isImage = (doc.mime || '').startsWith('image/') || (doc.name || '').match(/\.(png|jpg|jpeg|gif|webp|svg)$/i);
  const isPdf = (doc.mime || '') === 'application/pdf' || (doc.name || '').match(/\.pdf$/i);

  React.useEffect(() => {
    if (src && src.startsWith('data:')) {
      const url = base64ToBlobUrl(src);
      setBlobUrl(url);
      return () => { if (url) URL.revokeObjectURL(url); };
    }
  }, [src]);

  const renderSrc = blobUrl || src;

  if (!src) {
    return (
      <div className="rounded-lg border border-[#DCE4E0] bg-slate-50 p-8 text-center">
        <Eye className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="text-sm font-medium text-slate-600">No preview available</p>
        <p className="text-xs text-slate-400 mt-1">
          This document was uploaded before file preview was enabled. Ask the merchant to re-upload if needed.
        </p>
      </div>
    );
  }

  if (isImage && !imgError) {
    return (
      <div className="rounded-lg border border-[#DCE4E0] bg-white overflow-hidden p-4">
        <img
          src={renderSrc}
          alt={doc.name}
          className="w-full max-h-[500px] object-contain rounded"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  if (isImage && imgError) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
        <p className="text-sm font-medium text-amber-700">Image could not be loaded</p>
        <p className="text-xs text-amber-500 mt-1">The file may be corrupted. Ask the merchant to re-upload this document.</p>
        {blobUrl && (
          <a href={blobUrl} download={doc.name || 'document'} className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-lg bg-slate-600 text-white text-sm font-medium hover:bg-slate-700 transition-colors">
            <Download className="w-3.5 h-3.5" /> Download file
          </a>
        )}
      </div>
    );
  }
  if (isPdf) {
    if (!blobUrl) {
      return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
          <p className="text-sm font-medium text-amber-700">PDF could not be rendered</p>
          <p className="text-xs text-amber-500 mt-1">The file may be corrupted. Ask the merchant to re-upload this document.</p>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-[#DCE4E0] bg-white overflow-hidden">
        <iframe src={blobUrl} title={doc.name} className="w-full h-[500px] border-0" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-[#DCE4E0] bg-white p-8 text-center">
      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <p className="font-medium text-slate-600">Preview not available for this file type</p>
      {blobUrl ? (
        <a href={blobUrl} download={doc.name || 'document'} className="inline-flex items-center gap-1.5 mt-3 text-[#0B6445] hover:underline text-sm">
          <Download className="w-3.5 h-3.5" /> Download file
        </a>
      ) : (
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-[#0B6445] hover:underline text-sm mt-2 inline-block">
          Open in new tab
        </a>
      )}
    </div>
  );
}

const STATUS_BADGE = {
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  info_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  co_recommended_approve: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  co_recommended_reject: 'bg-red-50 text-red-700 border-red-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_LABELS = {
  pending: 'Pending',
  in_review: 'In Review',
  info_requested: 'Info Requested',
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  approved: 'Approved',
  rejected: 'Rejected',
};
const statusLabel = (s) => STATUS_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function daysBetween(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return diff < 0 ? 0 : diff;
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
}

function formatDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
}

function InfoRow({ label, value, icon: Icon }) {
  if (!value || value === '—') return null;
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center">
        {Icon ? <Icon className="w-4 h-4 text-slate-400" /> : <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-[#0F172A] mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#DCE4E0] rounded-xl overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4.5 h-4.5 text-[#0B6445]" />}
          <span className="font-semibold text-sm text-[#073B2E]">{title}</span>
          {badge && <Badge variant="outline" className="ml-2 text-xs">{badge}</Badge>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-5 py-4 bg-white">{children}</div>}
    </div>
  );
}

/* ── Queue list view ── */
function QueueList({ items, loading, onSelect, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-[#0B6445]" />
            My Queue
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">KYC / KYB applications for your compliance review.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="border-[#DCE4E0] shadow-sm">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-[#0E1B16]/60">
              <Inbox className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-base font-medium">No items in your queue</p>
              <p className="text-sm mt-1">Check back later for new assignments.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#DCE4E0]">
                    <th className="text-left py-3 px-3 font-semibold text-[#073B2E]">Merchant</th>
                    <th className="text-left py-3 px-3 font-semibold text-[#073B2E]">Email</th>
                    <th className="text-left py-3 px-3 font-semibold text-[#073B2E]">Submitted</th>
                    <th className="text-left py-3 px-3 font-semibold text-[#073B2E]">Status</th>
                    <th className="text-right py-3 px-3 font-semibold text-[#073B2E]">Days Open</th>
                    <th className="text-center py-3 px-3 font-semibold text-[#073B2E]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                      <td className="py-3 px-3">
                        <div className="font-medium text-[#0F172A]">{row.name || row.company_name || '—'}</div>
                        {row.company_name && row.name !== row.company_name && (
                          <div className="text-xs text-slate-500">{row.company_name}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-slate-600">{row.email || '—'}</td>
                      <td className="py-3 px-3 text-slate-600">
                        {row.submitted_at || row.created_at ? new Date(row.submitted_at || row.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-3">
                        <Badge variant="outline" className={STATUS_BADGE[row.kyc_status] || STATUS_BADGE.pending}>
                          {statusLabel(row.kyc_status)}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums font-medium">
                        {daysBetween(row.submitted_at || row.created_at)}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <Button size="sm" variant="outline" onClick={() => onSelect(row)} className="border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]">
                          <Eye className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Full Review View ── */
function ReviewDetail({ merchant: queueItem, onBack, onReviewSubmitted, api }) {
  const [kybData, setKybData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docReviews, setDocReviews] = useState({});
  const [docActionLoading, setDocActionLoading] = useState(null);
  const [docUpdateDialog, setDocUpdateDialog] = useState(null);
  const [docUpdateComment, setDocUpdateComment] = useState('');
  const [expandedInfoReqs, setExpandedInfoReqs] = useState({});

  const merchantId = queueItem?.id || queueItem?.user_id;

  const fetchKybData = useCallback(() => {
    if (!merchantId) return;
    setLoading(true);
    api.get(`/backoffice/users/${merchantId}/kyb-details`)
      .then(res => setKybData(res.data))
      .catch(() => toast.error('Failed to load merchant KYB/C details'))
      .finally(() => setLoading(false));
  }, [merchantId, api]);

  useEffect(() => {
    fetchKybData();
  }, [fetchKybData]);

  useEffect(() => {
    if (kybData?.document_reviews) setDocReviews(kybData.document_reviews);
  }, [kybData]);

  const handleAction = async (recommendation) => {
    if (!comments.trim()) {
      toast.error('Comments are required for all compliance actions');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/backoffice/kyc/${merchantId}/co-review`, {
        recommendation,
        comments: comments.trim(),
      });
      toast.success(`Review submitted: ${recommendation.replace(/_/g, ' ')}`);
      onReviewSubmitted();
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-[#0B6445]" />
      </div>
    );
  }

  const ob = kybData?.onboarding || {};
  const company = kybData?.company_information || {};
  const merchant = kybData?.merchant || {};
  const biz = ob.business || {};
  const address = ob.address || {};
  const ubos = ob.beneficial_owners || [];
  const uboIdDetails = ob.ubo_id_details || {};
  const uboVerified = ob.ubo_verified || {};
  const docs = ob.supporting_documents || [];

  const handleDocReview = async (docType, action, comment) => {
    setDocActionLoading(docType);
    try {
      const res = await api.post(`/backoffice/kyc/${merchantId}/document-review`, {
        doc_type: docType,
        action,
        comment: comment || '',
      });
      setDocReviews(prev => ({ ...prev, [docType]: res.data.review }));
      toast.success(action === 'accepted' ? 'Document verified and accepted' : 'Update request sent to merchant');
      setDocUpdateDialog(null);
      setDocUpdateComment('');
    } catch {
      toast.error('Failed to submit document review');
    } finally {
      setDocActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-[#073B2E] hover:bg-[#E6F7F3] -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Queue
          </Button>
          <Button variant="outline" size="sm" onClick={fetchKybData} className="border-[#DCE4E0] text-[#0B6445] hover:bg-[#E6F7F3]">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        <Badge variant="outline" className={`text-sm px-3 py-1 ${STATUS_BADGE[merchant.kyc_status] || STATUS_BADGE.pending}`}>
          {statusLabel(merchant.kyc_status)}
        </Badge>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-[#073B2E]">
          KYB / KYC Review — {merchant.name || queueItem.name || queueItem.email}
        </h1>
        <p className="text-[#0E1B16]/70 mt-1">
          Full compliance review for merchant application. Review all sections below before submitting your recommendation.
        </p>
      </div>

      {!kybData?.submitted && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">This merchant has not yet completed their KYB/KYC onboarding submission. Limited information available.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — details */}
        <div className="lg:col-span-2 flex flex-col gap-8">

          {/* Merchant Identity */}
          <CollapsibleSection title="Merchant Identity" icon={User}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow label="Contact Name" value={merchant.name} icon={User} />
              <InfoRow label="Email" value={merchant.email} icon={Mail} />
              <InfoRow label="Role" value={merchant.role} icon={Shield} />
              <InfoRow label="Account Status" value={merchant.account_status} icon={Shield} />
              <InfoRow label="Registered" value={formatDate(merchant.created_at)} icon={Calendar} />
              {merchant.assigned_co_name && (
                <InfoRow label="Assigned CO" value={merchant.assigned_co_name} icon={User} />
              )}
            </div>
          </CollapsibleSection>

          {/* Business Information */}
          <CollapsibleSection title="Business / Entity Information" icon={Building2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <InfoRow label="Entity Name" value={company.entity_name || biz.EntityName || biz.entity_name} icon={Building2} />
              <InfoRow label="Entity Type" value={company.entity_type || ob.entity_type || biz.EntityType} icon={FileText} />
              <InfoRow label="ABN" value={company.abn || biz.Abn} icon={FileText} />
              {company.acn && <InfoRow label="ACN" value={company.acn} icon={FileText} />}
              <InfoRow label="State" value={company.state || biz.State} icon={MapPin} />
              <InfoRow label="Postcode" value={company.postcode || biz.Postcode} icon={MapPin} />
              <InfoRow label="Location" value={company.location || biz.Location} icon={MapPin} />
              {company.gst_from && <InfoRow label="GST Registered From" value={company.gst_from} icon={Calendar} />}
              <InfoRow label="Industry" value={ob.industry} icon={Building2} />
              <InfoRow label="Monthly Revenue" value={ob.monthly_revenue} icon={DollarSign} />
              <InfoRow label="Products / Services" value={ob.products_services} icon={FileText} />
              <InfoRow label="Third Party Shareholding" value={ob.third_party_shareholding} />
            </div>
            {ob.websites && ob.websites.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#DCE4E0]">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Websites</p>
                <div className="flex flex-wrap gap-2">
                  {ob.websites.map((w, i) => (
                    <Badge key={i} variant="outline" className="text-xs font-mono bg-slate-50">
                      <Globe className="w-3 h-3 mr-1" />{w}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {ob.business_countries && ob.business_countries.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#DCE4E0]">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Countries of Operation</p>
                <div className="flex flex-wrap gap-1.5">
                  {ob.business_countries.map((c, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CollapsibleSection>

          {/* Business Address */}
          {(address.street || address.city || address.line1) && (
            <CollapsibleSection title="Business Address" icon={MapPin}>
              <div className="rounded-lg border border-[#DCE4E0] bg-[#FAFCFB] p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
                  <InfoRow label="Street / Line 1" value={address.street || address.line1} icon={MapPin} />
                  <InfoRow label="Line 2" value={address.line2} icon={MapPin} />
                  <InfoRow label="City / Suburb" value={address.city || address.suburb} icon={MapPin} />
                  <InfoRow label="State" value={address.state} icon={MapPin} />
                  <InfoRow label="Postcode" value={address.postcode || address.zip} icon={MapPin} />
                  <InfoRow label="Country" value={address.country} icon={Globe} />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Beneficial Owners / UBO */}
          <CollapsibleSection title="Beneficial Owners (UBO)" icon={Users} badge={ubos.length > 0 ? `${ubos.length} declared` : 'None'}>
            {ubos.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">No beneficial owners declared.</p>
            ) : (
              <div className="space-y-4">
                {ubos.map((ubo, idx) => {
                  const idDetail = uboIdDetails[ubo.id] || {};
                  const isVerified = uboVerified[ubo.id] === true;
                  return (
                    <div key={ubo.id || idx} className={`rounded-lg border p-4 ${isVerified ? 'border-emerald-200 bg-emerald-50/30' : 'border-[#DCE4E0] bg-[#FAFCFB]'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[#0B6445]" />
                          <span className="font-semibold text-sm text-[#073B2E]">UBO #{idx + 1}: {ubo.name || '—'}</span>
                          {ubo.role && <Badge variant="outline" className="text-xs">{ubo.role}</Badge>}
                        </div>
                        {isVerified ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium border border-emerald-200">
                            <CheckCircle className="w-3.5 h-3.5" /> Electronically Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium border border-amber-200">
                            <AlertTriangle className="w-3.5 h-3.5" /> Not Verified
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <InfoRow label="Date of Birth" value={ubo.dob || ubo.date_of_birth} icon={Calendar} />
                        <InfoRow label="Nationality" value={ubo.nationality} icon={Globe} />
                        <InfoRow label="Ownership %" value={ubo.ownership_percentage ? `${ubo.ownership_percentage}%` : null} icon={DollarSign} />
                        <InfoRow label="Address" value={ubo.address || ubo.residential_address} icon={MapPin} />
                        {idDetail.id_type && <InfoRow label="ID Type" value={idDetail.id_type} icon={FileText} />}
                        {idDetail.id_number && <InfoRow label="ID Number" value={idDetail.id_number} icon={FileText} />}
                        {idDetail.id_expiry && <InfoRow label="ID Expiry" value={idDetail.id_expiry} icon={Calendar} />}
                        {idDetail.id_country && <InfoRow label="ID Country" value={idDetail.id_country} icon={Globe} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleSection>

          {/* Supporting Documents */}
          <CollapsibleSection title="Supporting Documents" icon={FileText} badge={docs.length > 0 ? `${docs.length} uploaded` : 'None'}>
            {docs.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">No supporting documents uploaded.</p>
            ) : (
              <>
              <div className="space-y-3">
                {docs.map((doc, idx) => {
                  const docType = doc.type || doc.category || 'other';
                  const docTypeLabel = getDocTypeLabel(docType);
                  const fileSize = doc.size
                    ? doc.size >= 1048576
                      ? `${(doc.size / 1048576).toFixed(1)} MB`
                      : `${(doc.size / 1024).toFixed(1)} KB`
                    : '';
                  const review = docReviews[docType];
                  const isAccepted = review?.action === 'accepted';
                  const isUpdateRequested = review?.action === 'update_requested';

                  return (
                    <div key={idx} className={`flex items-center justify-between py-3 px-4 rounded-lg border transition-colors ${
                      isAccepted ? 'border-emerald-300 bg-emerald-50/40' :
                      isUpdateRequested ? 'border-amber-300 bg-amber-50/30' :
                      'border-[#DCE4E0] bg-[#FAFCFB]'
                    } hover:bg-white`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isAccepted ? 'bg-emerald-100' : 'bg-[#E6F7F3]'
                        }`}>
                          {isAccepted ? <CheckCircle className="w-4.5 h-4.5 text-emerald-600" /> : <FileText className="w-4.5 h-4.5 text-[#0B6445]" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-[#0F172A] truncate">{doc.name || doc.filename || `Document ${idx + 1}`}</p>
                            {isAccepted && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                <CheckCircle className="w-3 h-3" /> Verified & Accepted
                              </span>
                            )}
                            {isUpdateRequested && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                <AlertTriangle className="w-3 h-3" /> Update Requested
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{docTypeLabel}{fileSize ? ` • ${fileSize}` : ''}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0 ml-3 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] gap-1.5"
                        onClick={() => setViewingDoc(doc)}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Document Update Request Dialog */}
              {docUpdateDialog && (
                <Dialog open={!!docUpdateDialog} onOpenChange={(open) => { if (!open) { setDocUpdateDialog(null); setDocUpdateComment(''); } }}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#0F172A]">Request Document Update</DialogTitle>
                      <DialogDescription className="text-slate-600">
                        Explain what needs to be updated for: <strong>{docUpdateDialog.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</strong>
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-3 space-y-3">
                      <Textarea
                        placeholder="E.g. Document is blurry, please re-upload in higher resolution..."
                        value={docUpdateComment}
                        onChange={(e) => setDocUpdateComment(e.target.value)}
                        className="min-h-[100px] text-sm"
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setDocUpdateDialog(null); setDocUpdateComment(''); }}
                          className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
                          Cancel
                        </Button>
                        <Button size="sm" disabled={!docUpdateComment.trim() || !!docActionLoading}
                          onClick={() => handleDocReview(docUpdateDialog, 'update_requested', docUpdateComment.trim())}
                          className="bg-amber-600 hover:bg-amber-700 text-white">
                          {docActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </>
            )}
          </CollapsibleSection>

          {/* Additional information (info requests + merchant replies + response docs) */}
          {(() => {
            const infoReqs = kybData?.information_requests || [];
            if (infoReqs.length === 0) return null;
            const responseDocTypes = (r) => (r.response_document_types || []).map((t) => (t || '').toLowerCase());
            return (
              <CollapsibleSection title="Additional information" icon={MessageSquare} badge={`${infoReqs.length} request(s)`} defaultOpen={true}>
                <div className="space-y-8 pt-1">
                  {infoReqs.map((req) => {
                    const respTypes = responseDocTypes(req);
                    const responseDocs = respTypes.length
                      ? docs.filter((d) => {
                          const t = (d.type || d.category || '').toLowerCase();
                          return t && respTypes.includes(t);
                        })
                      : [];
                    const isResponded = req.status === 'responded';
                    const isExpanded = isResponded ? (expandedInfoReqs[req.id] === true) : true;
                    return (
                      <div key={req.id} className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden">
                        <button
                          type="button"
                          className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${isResponded ? 'cursor-pointer hover:bg-slate-50/50' : 'cursor-default'}`}
                          onClick={() => isResponded && setExpandedInfoReqs((p) => ({ ...p, [req.id]: !p[req.id] }))}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Badge variant="outline" className={`shrink-0 ${isResponded ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                              {isResponded ? 'Responded' : 'Open'}
                            </Badge>
                            <span className="text-sm text-slate-700 truncate">{req.message}</span>
                          </div>
                          <span className="text-xs text-slate-400 shrink-0">{formatDate(req.created_at)}</span>
                          {isResponded && (
                            <span className="shrink-0 text-slate-400">
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </span>
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-200/60">
                            {req.requested_by_name && (
                              <p className="text-xs text-slate-500 pt-3">Requested by: {req.requested_by_name}</p>
                            )}
                            <p className="text-sm font-medium text-slate-800">{req.message}</p>
                            {req.status === 'responded' && req.merchant_response && (
                              <div className="rounded-lg bg-emerald-50/80 border border-emerald-200 p-3">
                                <p className="text-xs font-medium text-emerald-700 mb-1">Merchant response:</p>
                                <p className="text-sm text-emerald-800">{req.merchant_response}</p>
                              </div>
                            )}
                            {req.status === 'responded' && responseDocs.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-slate-600">Documents attached to response:</p>
                                <div className="space-y-2">
                                  {responseDocs.map((doc, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[#DCE4E0] bg-white">
                                      <span className="text-sm text-slate-700 truncate">{doc.name || doc.filename || getDocTypeLabel(doc.type || doc.category) || `Document ${idx + 1}`}</span>
                                      <Button type="button" size="sm" variant="outline" className="shrink-0 ml-2 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3]" onClick={() => setViewingDoc(doc)}>
                                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            );
          })()}

          {/* Document Viewer Dialog */}
          {viewingDoc && (
            <Dialog open={!!viewingDoc} onOpenChange={(open) => { if (!open) setViewingDoc(null); }}>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
                    <FileText className="w-5 h-5 text-[#0B6445]" />
                    Document Preview
                  </DialogTitle>
                  <DialogDescription className="text-slate-600">
                    {viewingDoc.name || viewingDoc.filename || 'Document'}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-2 space-y-4">
                  <div className="rounded-lg border border-[#DCE4E0] bg-[#F8FAFC] p-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">File Name</p>
                        <p className="text-sm text-[#0F172A] mt-0.5 font-medium">{viewingDoc.name || viewingDoc.filename || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Document Type</p>
                        <p className="text-sm text-[#0F172A] mt-0.5 font-medium">
                          {(viewingDoc.type || viewingDoc.category || 'other').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">File Size</p>
                        <p className="text-sm text-[#0F172A] mt-0.5">
                          {viewingDoc.size
                            ? viewingDoc.size >= 1048576
                              ? `${(viewingDoc.size / 1048576).toFixed(1)} MB`
                              : `${(viewingDoc.size / 1024).toFixed(1)} KB`
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</p>
                        {(() => {
                          const sDocType = viewingDoc.type || viewingDoc.category || 'other';
                          const sReview = docReviews[sDocType];
                          if (sReview?.action === 'accepted') return (
                            <Badge variant="outline" className="mt-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" /> Verified
                            </Badge>
                          );
                          if (sReview?.action === 'update_requested') return (
                            <Badge variant="outline" className="mt-0.5 bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Update Requested
                            </Badge>
                          );
                          return (
                            <Badge variant="outline" className="mt-0.5 bg-blue-50 text-blue-700 border-blue-200 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" /> Pending Review
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <DocPreview doc={viewingDoc} />
                </div>
                {/* Action buttons inside dialog */}
                {(() => {
                  const vDocType = viewingDoc.type || viewingDoc.category || 'other';
                  const vReview = docReviews[vDocType];
                  const vIsAccepted = vReview?.action === 'accepted';
                  const vIsUpdateRequested = vReview?.action === 'update_requested';
                  const vIsLoading = docActionLoading === vDocType;

                  return (
                    <div className="mt-4 space-y-3">
                      {vIsAccepted && (
                        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-emerald-800">Verified & Accepted</span>
                          {vReview?.reviewed_by_name && <span className="text-xs text-emerald-600 ml-auto">by {vReview.reviewed_by_name}</span>}
                        </div>
                      )}
                      {vIsUpdateRequested && vReview?.comment && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                          <p className="text-xs font-medium text-amber-700 mb-0.5">Update requested:</p>
                          <p className="text-sm text-amber-800">{vReview.comment}</p>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex gap-2">
                          {!vIsAccepted && (
                            <>
                              <Button
                                size="sm"
                                disabled={vIsLoading}
                                onClick={() => handleDocReview(vDocType, 'accepted', '')}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 px-4"
                              >
                                {vIsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Accept Document</>}
                              </Button>
                              {vIsUpdateRequested ? (
                                <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 h-9">
                                  <Clock className="w-3.5 h-3.5" />
                                  Update already requested
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={vIsLoading}
                                  onClick={() => { setDocUpdateDialog(vDocType); setDocUpdateComment(''); }}
                                  className="border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-900 text-xs h-9 px-4"
                                >
                                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                                  Request Update
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setViewingDoc(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 h-9">
                          Close
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </DialogContent>
            </Dialog>
          )}

          {/* Declaration */}
          <CollapsibleSection title="Declaration & Signature" icon={Shield} defaultOpen={false}>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ob.declaration_accepted ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                  {ob.declaration_accepted ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                </div>
                <span className="text-sm text-[#0F172A]">Declaration: {ob.declaration_accepted ? 'Accepted' : 'Not accepted'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ob.review_declaration_accepted ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                  {ob.review_declaration_accepted ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                </div>
                <span className="text-sm text-[#0F172A]">Review declaration: {ob.review_declaration_accepted ? 'Accepted' : 'Not accepted'}</span>
              </div>
              {ob.signature_data && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Signature</p>
                  <div className="border border-[#DCE4E0] rounded-lg p-3 bg-white inline-block">
                    <img src={ob.signature_data} alt="Merchant signature" className="max-h-20" />
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>

        {/* Right column — actions + history */}
        <div className="space-y-6">
          {/* Review Action Panel */}
          <Card className="border-[#0B6445]/30 shadow-sm sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#073B2E] flex items-center gap-2">
                <Shield className="w-4.5 h-4.5 text-[#0B6445]" />
                Compliance Decision
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const ks = merchant.kyc_status;
                const isFinal = ks === 'approved' || ks === 'rejected';
                const isRecommended = ks === 'co_recommended_approve' || ks === 'co_recommended_reject';

                if (isFinal) {
                  return (
                    <div className={`rounded-lg p-4 text-center ${ks === 'approved' ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        {ks === 'approved' ? <CheckCircle className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
                        <span className={`text-lg font-semibold ${ks === 'approved' ? 'text-emerald-700' : 'text-red-700'}`}>
                          KYC {ks === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">Final decision made by Super Admin.</p>
                    </div>
                  );
                }

                if (isRecommended) {
                  const isApproval = ks === 'co_recommended_approve';
                  return (
                    <div className={`rounded-lg p-4 text-center ${isApproval ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        {isApproval ? <CheckCircle className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
                        <span className={`text-lg font-semibold ${isApproval ? 'text-emerald-700' : 'text-red-700'}`}>
                          {isApproval ? 'Recommended Approve' : 'Recommended Reject'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Your recommendation has been submitted. Awaiting Super Admin final decision.</p>
                      {merchant.kyc_last_co_comments && (
                        <div className="mt-3 rounded-md bg-white/60 border border-slate-200 p-2.5 text-left">
                          <p className="text-xs font-medium text-slate-500 mb-0.5">Your comments:</p>
                          <p className="text-xs text-slate-700">{merchant.kyc_last_co_comments}</p>
                        </div>
                      )}
                    </div>
                  );
                }

                const docs = (kybData?.onboarding || {}).supporting_documents || [];
                const allDocsVerified = docs.length > 0 && docs.every((doc) => {
                  const docType = doc.type || doc.category || 'other';
                  const r = docReviews[docType];
                  return r?.action === 'accepted';
                });
                const canRecommendApprove = allDocsVerified && comments.trim().length > 0;

                return (
                  <>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1.5">
                        Review Comments <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        placeholder="Document your findings, risks, and rationale for your recommendation…"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        className="min-h-[120px] text-sm"
                      />
                      <p className="text-xs text-slate-400 mt-1">Required for all actions. Be specific about compliance findings.</p>
                    </div>

                    {docs.length > 0 && !allDocsVerified && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        Verify and accept all supporting documents above before recommending approval.
                      </p>
                    )}

                    <div className="space-y-2">
                      <Button
                        onClick={() => handleAction('approve')}
                        disabled={actionLoading || !canRecommendApprove}
                        className="w-full bg-[#0B6445] hover:bg-[#095b3d] text-white justify-center disabled:opacity-50 disabled:pointer-events-none"
                        title={!canRecommendApprove && docs.length > 0 && !allDocsVerified ? 'Verify and accept all documents above first' : undefined}
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-2" />Recommend Approve</>}
                      </Button>
                      <Button
                        onClick={() => handleAction('reject')}
                        disabled={actionLoading || !comments.trim()}
                        variant="outline"
                        className="w-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-900 justify-center"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-2" />Recommend Reject</>}
                      </Button>
                      <Button
                        onClick={() => handleAction('request_info')}
                        disabled={actionLoading || !comments.trim()}
                        variant="outline"
                        className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-900 justify-center"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4 mr-2" />Request More Info</>}
                      </Button>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function BackOfficeComplianceQueue() {
  const { api } = useAuth();
  const { isLive } = useEnvironment();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/kyc/my-queue');
      setItems(res.data?.data || []);
    } catch {
      setItems([]);
      toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue, isLive]);

  if (selectedMerchant) {
    return (
      <BackOfficeLayout>
        <ReviewDetail
          merchant={selectedMerchant}
          api={api}
          onBack={() => setSelectedMerchant(null)}
          onReviewSubmitted={() => {
            setSelectedMerchant(null);
            fetchQueue();
          }}
        />
      </BackOfficeLayout>
    );
  }

  return (
    <BackOfficeLayout>
      <QueueList items={items} loading={loading} onSelect={setSelectedMerchant} onRefresh={fetchQueue} />
    </BackOfficeLayout>
  );
}
